import Button from "@components/common/Button";
import Spinner from "@components/common/Spinner";
import useAppContext from "@hooks/useAppContext";
import InputField from "@libs/forms/InputField";
import InputPhone from "@libs/forms/InputPhone";
import InputTextarea from "@libs/forms/InputTextarea";
import { MAX_LENGTH } from "@libs/forms/utils/constants";
import {
  sanitizeDescription,
  sanitizeEmail,
  sanitizeName,
} from "@libs/forms/utils/sanitations";
import { Turnstile } from "@marsidev/react-turnstile";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";

interface IContactFormFields {
  showButtonSpinner: boolean;
}

const ContactFormFields = ({
  showButtonSpinner,
}: IContactFormFields): JSX.Element => {
  const formContext = useFormContext();
  const { isMobile } = useAppContext();

  const [disabled, setDisabled] = useState(true);

  useEffect(() => {
    const errors = Object.values(formContext.formState.errors);
    if (errors?.length > 0) {
      // Scroll user to top input field with an error
      if (isMobile) {
        window.scrollTo({
          top:
            Number(window.scrollY) +
            Number(errors[0]?.ref?.getBoundingClientRect()?.top) -
            80,
        });
      } else {
        // On desktop just make the whole form visible
        window.scrollTo(0, 0);
      }
    }
  }, [formContext.formState.errors]);

  const handleTurnstileSuccess = (token: string): void => {
    formContext.setValue("turnstile_token", token);
    setDisabled(false);
  };

  const handleButtonClick = async (event: { preventDefault: () => void }) => {
    const isValid = await formContext.trigger();

    if (!isValid) {
      event.preventDefault();
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <InputField
        name="fullName"
        label="Full Name"
        type="name"
        hasAsterisk={true}
        maxLength={MAX_LENGTH.name}
        sanitize={sanitizeName}
        isContactForm
      />
      <InputField
        name="email"
        label="Email"
        type="email"
        hasAsterisk={true}
        maxLength={MAX_LENGTH.email}
        sanitize={sanitizeEmail}
        isContactForm
      />
      <InputPhone name="phoneNumber" label="Phone" isContactForm />
      <InputTextarea
        name="description"
        label="Describe your dream vacation"
        maxLength={MAX_LENGTH.description}
        sanitize={sanitizeDescription}
        isContactForm
      />
      <div className="flex justify-center">
        <Turnstile
          siteKey="0x4AAAAAAAczbdBXsLOvdSke"
          options={{ theme: "light" }}
          onSuccess={handleTurnstileSuccess}
        />
      </div>
      <div className="mx-auto mt-1 w-[17.5rem] desktop:mt-4 desktop:w-[20rem]">
        <Button
          type="submit"
          variant="primary"
          id="submit-btn"
          size="medium"
          onClick={handleButtonClick}
          disabled={disabled}
        >
          <>
            {showButtonSpinner ? (
              <Spinner
                width={24}
                height={24}
                mainColor={"#F55B46"} // the orange color from the theme
                backgroundColor={"#F3F4F8"} // the gray color from the theme
                position={"center"}
              />
            ) : (
              "Contact Us"
            )}
          </>
        </Button>
      </div>
    </div>
  );
};

export default ContactFormFields;
